// frontend/src/utils/analytics.ts
/**
 * Google Analytics 4 utilities
 * GA4 Measurement ID should be set in environment variable VITE_GA4_ID
 *
 * Share links (/s/<token>, design 8.1 and 8.6) never reach Google Analytics:
 * GA sends the full URL as page_location, and the token is a bearer link to
 * the owner's photos.
 * - initGA4 does nothing when the page was first loaded on a share path.
 * - trackPageView and trackEvent do nothing on a share path.
 * - Once GA is running, GA's own page views (enhanced measurement's history
 *   events) are switched off with the documented `ga-disable-<id>` flag before
 *   the URL changes to a share path, and on again when it leaves one.
 */

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

/** /s/<token>, in any case (the router and nginx match it case-insensitively). */
const SHARE_PATH = /^\/+s\//i;

/**
 * Tested on the decoded path, as the router matches it (/%73/<token> renders
 * the share page too). A path that won't decode counts as a share path, to be safe.
 * index.html repeats this test before the app loads.
 */
export function isSharePath(pathname: string): boolean {
  let decoded: string;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    return true;
  }
  return SHARE_PATH.test(decoded);
}

function onSharePath(): boolean {
  return typeof window !== 'undefined' && isSharePath(window.location.pathname);
}

function pathOf(url: string | URL | null | undefined): string {
  if (url === null || url === undefined) return window.location.pathname;
  try {
    return new URL(String(url), window.location.href).pathname;
  } catch {
    return window.location.pathname;
  }
}

/**
 * Keeps `window['ga-disable-<id>']` true while the URL is a share path. Set
 * before history.pushState/replaceState change the URL (GA wraps these too,
 * and reads the flag when it sends), and on popstate, whose listener is added
 * here before GA's script loads, so it runs first.
 */
function guardSharePaths(measurementId: string): void {
  const flag = `ga-disable-${measurementId}`;
  const store = window as unknown as Record<string, unknown>;
  const sync = (pathname: string): void => {
    store[flag] = isSharePath(pathname);
  };
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = window.history[method];
    window.history[method] = function guarded(
      this: History,
      data: unknown,
      unused: string,
      url?: string | URL | null
    ): void {
      sync(pathOf(url));
      original.call(this, data, unused, url);
    };
  }
  window.addEventListener('popstate', () => sync(window.location.pathname));
  sync(window.location.pathname);
}

export function initGA4(): void {
  const measurementId = import.meta.env.VITE_GA4_ID;

  if (!measurementId) {
    console.warn('GA4 Measurement ID not configured');
    return;
  }

  // Never on a share link's first load (main.tsx checks this too).
  if (onSharePath()) return;

  guardSharePaths(measurementId);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(): void {
    // gtag.js reads the Arguments object itself, not an array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Configure GA4
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false, // We'll track page views manually for SPA
  });
}

export function trackPageView(path: string, title: string): void {
  if (isSharePath(path) || onSharePath()) return;
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    });
  }
}

export function trackEvent(eventName: string, params: Record<string, unknown> = {}): void {
  // GA attaches the current URL to every event.
  if (onSharePath()) return;
  if (window.gtag) {
    window.gtag('event', eventName, params);
  }
}
